import {
    ChartConfigItem,
    ChartVisualizationConfig,
    ChartVisualizationType,
    CreateConfig,
    GridConfigItem,
    GridVisualizationConfig,
    GridVisualizationType,
    VisualizationConfig,
    VisualizationConfigs,
    VizConfigs,
} from '@ddv/models';

export function getVisualizationConfigs(visualizationConfigs: VisualizationConfigs[]): VisualizationConfig[] {
    return visualizationConfigs.map((config) => {
        if (isGridVisualizationType(config.visualizationType)) {
            return getGridVisualizationConfig(config);
        }

        return getChartVisualizationConfig(config);
    });
}

function getGridVisualizationConfig(config: VisualizationConfigs): GridVisualizationConfig {
    return {
        isDefault: !!config.isDefault,
        visualizationType: config.visualizationType as GridVisualizationType,
        alternateRowShading: !!config.alternateRowShading,
        autoAddDynamicColumns: !!config.autoAddDynamicColumns,
        autoSizeColumns: !!config.autoSizeColumns,
        distinctRowsOnly: !!config.distinctRowsOnly,
        showHyperlinks: !!config.showHyperlinks,
        hideGridHeaders: !!config.hideGridHeaders,
        showColumnHeaderTooltip: !!config.showColumnHeaderTooltip,
        showGrandTotal: !!config.showGrandTotal,
        showGrouperCount: config.showGrouperCount ?? null,
        showQuickSearch: config.showQuickSearch ?? null,
        showSubTotal: config.showSubTotal ?? null,
        showTotalAtTop: !!config.showTotalAtTop,
        groupDefaultExpanded: config.groupDefaultExpanded ?? null,
        groupSort: config.groupSort ?? null,
        groupSortIndex: config.groupSortIndex ?? null,
        isPivotMode: !!config.isPivotMode,
        repeatGroupData: config.repeatGroupData ?? null,
        rowHeight: config.rowHeight ?? 20,
        useCustomHeaderNames: config.useCustomHeaderNames ?? null,
        configs: getGridConfigs(config.configs),
    };
}

function getChartVisualizationConfig(config: VisualizationConfigs): ChartVisualizationConfig {
    return {
        isDefault: !!config.isDefault,
        visualizationType: config.visualizationType as ChartVisualizationType,
        enableLegend: !!config.enableLegend,
        legendPosition: config.legendPosition ?? 'top',
        enableInsideLabel: !!config.enableInsideLabel,
        enableLabels: !!config.enableLabels,
        labelOrientation: config.labelOrientation ?? null,
        labelStyle: config.labelStyle ?? null,
        enableTotalLabel: !!config.enableTotalLabel,
        enableTooltip: !!config.enableTooltip,
        enableSliceManagement: !!config.enableSliceManagement,
        sliceManagementType: config.sliceManagementType ?? null,
        groupByMaxCount: config.groupByMaxCount ?? null,
        groupByPercent: config.groupByPercent ?? null,
        displayTotal: !!config.displayTotal,
        highlightSlice: config.highlightSlice ?? null,
        numberFormat: config.numberFormat ?? 'XX',
        decimalPlaces: config.decimalPlaces ?? 0,
        numberUnits: config.numberUnits ?? 'actual',
        gridLineStyle: config.gridLineStyle ?? null,
        inChartLabel: !!config.inChartLabel,
        showHoverLines: !!config.showHoverLines,
        showBrush: config.showBrush ?? null,
        showOthers: config.showOthers ?? null,
        sortTableBy: config.sortTableBy ?? 'SLICER',
        tableSortDirection: config.tableSortDirection ?? 'ASC',
        axisConfigurations: config.axisConfigurations ?? [],
        zoomLevels: config.zoomLevels ?? null,
        configs: getChartConfigs(config.configs),
    };
}

function getGridConfigs(configs: VizConfigs | undefined): { values: GridConfigItem[] } {
    if (!configs?.values) {
        return { values: [] };
    }

    return {
        // eslint-disable-next-line complexity
        values: (configs.values as GridConfigItem[]).map((v) => {
            return {
                colId: v.colId,
                label: v.label ?? '',
                value: v.value,
                datatype: v.datatype ?? 'string',
                displayType: v.displayType ?? 'string',
                showCustomName: !!v.showCustomName,
                customName: v.customName,
                alignment: v.alignment ?? 'left',
                isHidden: !!v.isHidden,
                isRanked: !!v.isRanked,
                isBlank: !!v.isBlank,
                useAbsVal: !!v.useAbsVal,
                pinned: v.pinned ?? null,
                fontStyle: v.fontStyle ?? 'roman',
                linkBehavior: v.linkBehavior ?? null,
                linkType: v.linkType ?? null,
                width: v.width ?? 0,
                customWidthWhenGroup: v.customWidthWhenGroup ?? null,
                pivotIndex: v.pivotIndex ?? null,
                rowGroupIndex: v.rowGroupIndex ?? null,
                agFilter: v.agFilter ?? null,
                agSort: v.agSort ? { sort: v.agSort.sort ?? null } : null,
                agSortIndex: v.agSortIndex ?? null,
                columnCondition: v.columnCondition ?? [],
                aggregationType: v.aggregationType ?? undefined,
                numberFormat: v.numberFormat,
                decimalPlaces: v.decimalPlaces,
                negativeValue: v.negativeValue,
                dateFormat: v.dateFormat,
                rankOrder: v.rankOrder ?? undefined,
                hyperlink: v.hyperlink ?? undefined,
                ddvWidget: v.ddvWidget ?? undefined,
            };
        }),
    };
}

function getChartConfigs(configs: VizConfigs | undefined): CreateConfig {
    if (!configs?.values) {
        return { values: [] };
    }

    return {
        values: getChartConfigValues(configs.values as ChartConfigItem[]),
        slicers: configs.slicers,
        tooltips: configs.tooltips,
        axisTimes: configs.axisTimes,
        details: configs.details,
        mirroredValues: configs.mirroredValues,
    };
}

function getChartConfigValues(values: ChartConfigItem[]): ChartConfigItem[] {
    return values.map((v) => ({
        colId: v.colId,
        label: v.label ?? '',
        value: v.value,
        datatype: v.datatype ?? 'string',
        displayType: v.displayType ?? 'string',
        showCustomName: !!v.showCustomName,
        customName: v.customName,
        aggregationType: v.aggregationType ?? 'first',
        colorType: v.colorType,
        colorName: v.colorName,
        colorSortBy: v.colorSortBy,
        colorSortDirection: v.colorSortDirection,
        configCustomStyles: v.configCustomStyles,
    }));
}

function isGridVisualizationType(visualizationType: string): visualizationType is GridVisualizationType {
    return ['ADVANCED_GRID', 'SIMPLE_GRID'].includes(visualizationType);
}
